import React, { useState } from 'react';
import { Badge, Typography } from '@mui/material';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import { format } from 'date-fns';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import stripHTML from '../../../utils/stripHTML';
import useVerifyExtraction from '../../hooks/useExtractionVerify';
import { AppContext } from '../../../App';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

export interface ExtractionBatchSendProps {
  extractionId: string;
  ebLabel: string;
  ebFinished: boolean;
  type: 'menu' | 'icon';
  isExpired?: boolean;
  isVerified?: boolean;
  verifiedUpdatedAt?: string;
  setLoadingState?: Function;
  handleClose?: () => void;
}

const ExtractionBatchSend: React.FC<ExtractionBatchSendProps> = ({
  extractionId,
  ebLabel,
  ebFinished,
  isExpired,
  isVerified,
  verifiedUpdatedAt,
  setLoadingState,
  type,
  handleClose,
}) => {
  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [okButtonLock, setOkButtonLock] = useState(false);

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleCloseConfirmation = () => {
    setOpenDialog(false);
    runHandleClose();
  };

  const handleConfirm = async () => {
    setOkButtonLock(true);
    await verifyExtraction();
    setOpenDialog(false);
  };

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const { mutate: verifyExtraction } = useVerifyExtraction(extractionId, true, {
    onMutate: () => {
      if (setLoadingState) {
        setLoadingState(true);
      }
      runHandleClose();
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: `Error on verifying the batch${
          error.message ? ': ' + stripHTML(error.message) : null
        }`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['extractionBatches']
      });
      queryClient.invalidateQueries({
        queryKey: ['extractionBatch']
      });

      setToast({
        variant: 'success',
        open: true,
        message: `${ebLabel} Batch successfully Sent`,
      });
    },
    onSettled: () => {
      if (setLoadingState) {
        setLoadingState(false);
      }
    },
  });

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          key={'Send'}
          onClick={handleClickOpen}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
          disabled={!ebFinished}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Send</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <Badge
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          overlap="rectangular"
          invisible={!isVerified}
          sx={{
            '& .MuiBadge-badge': {
              // slight inset so it doesn't sit between adjacent icons
              right: 2,
            },
          }}
          badgeContent={
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#65BBA9',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.15)',
              }}
            >
              <DoneIcon sx={{ fontSize: 10, color: '#ffffff' }} />
            </span>
          }
        >
          <IconButtonWhiteBg
            disabled={!ebFinished}
            onClick={handleClickOpen}
            id="btn-send-eb"
            cypressId="btn-send-eb"
            tooltip={
              isVerified && verifiedUpdatedAt
                ? (() => {
                    const date = new Date(verifiedUpdatedAt);
                    return isNaN(date.getTime())
                      ? 'Send'
                      : `Sent: ${format(date, 'yyyy-MM-dd HH:mm:ss')}`;
                  })()
                : 'Send'
            }
          >
            <SendIcon style={{ fontSize: '13px', color: 'black' }} />
          </IconButtonWhiteBg>
        </Badge>
      ) : null}

      <DialogGlynt open={openDialog} closeModal={handleCloseConfirmation} cypressId="dialog-send-eb" loading={okButtonLock}>
        <DialogTitleGlynt title="Confirmation" closeModal={handleCloseConfirmation} loading={okButtonLock} />
        <DialogContentGlynt>
          <Typography variant="body2" color="textSecondary">Do you want to send now?</Typography>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleConfirm}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-send-eb-cancel"
          submitCypressId="btn-send-eb-ok"
          clearCypressId=""
          submitButtonText="Send"
          submitLoading={okButtonLock}
        />
      </DialogGlynt>
    </>
  );
};

export default ExtractionBatchSend;
